import { cleanupManager } from "./cleanupManager.js";
import { storageManager } from "./storageManager.js";
import {
  cleanupUploadedFiles,
  cleanupFailedFiles,
  cleanupOrphanedFiles,
} from "./fileUpload.js";
import {
  getStorageInfo,
  cleanupOldFiles,
  getStoredFilesInfo,
} from "./fileStorage.js";

/**
 * Cleanup Utilities for Development and Debugging
 * Expose cleanup functions to the global window object for manual use
 */

// Development utilities - only available in non-production environments
if (process.env.NODE_ENV !== "production") {
  window.artemisCleanup = {
    // Storage information
    async getStorageStatus() {
      const storageInfo = await getStorageInfo();
      const filesInfo = await getStoredFilesInfo();
      const cleanupStats = await cleanupManager.getCleanupStats();

      console.group("🗄️ Storage Status");
      console.log("Storage Info:", storageInfo);
      console.log("Files Info:", filesInfo);
      console.log("Cleanup Stats:", cleanupStats);
      console.groupEnd();

      return { storageInfo, filesInfo, cleanupStats };
    },

    // Manual cleanup functions
    async cleanupAll() {
      console.log("🧹 Starting full manual cleanup...");
      const result = await cleanupManager.performManualCleanup();
      console.log("✅ Full cleanup completed:", result);
      return result;
    },

    async cleanupOldFiles(days = 7) {
      console.log(`🗑️ Cleaning up files older than ${days} days...`);
      const result = await cleanupOldFiles(days);
      console.log(`✅ Cleaned up ${result} old files`);
      return result;
    },

    async cleanupOrphanedFiles() {
      console.log("👻 Cleaning up orphaned files...");
      const result = await cleanupOrphanedFiles();
      console.log("✅ Orphaned files cleanup:", result);
      return result;
    },

    async cleanupRoute(routeId) {
      console.log(`📋 Cleaning up route ${routeId}...`);
      const uploadedResult = await cleanupUploadedFiles(routeId);
      const failedResult = await cleanupFailedFiles(routeId);

      const result = {
        uploaded: uploadedResult,
        failed: failedResult,
      };

      console.log("✅ Route cleanup completed:", result);
      return result;
    },

    async cleanupByRoute(routeId) {
      console.log(`🗂️ Cleaning up files by route ${routeId}...`);
      const result = await cleanupUploadedFiles(routeId);
      console.log(
        `✅ Cleaned up ${result.deletedFiles || 0} files for route ${routeId}`
      );
      return result;
    },

    // Storage monitoring
    async monitorStorage() {
      const report = await storageManager.getStorageReport();
      console.group("📊 Storage Report");
      console.log("Status:", report.status);
      console.log("Usage:", `${report.usagePercent}%`);
      console.log(
        "Available:",
        storageManager.constructor.formatBytes(report.available)
      );
      console.log("Files:", report.files);
      console.log("Recommendations:", report.recommendations);
      console.groupEnd();
      return report;
    },

    // Cleanup manager controls
    stopCleanupManager() {
      cleanupManager.stop();
      console.log("🛑 Cleanup manager stopped");
    },

    startCleanupManager() {
      cleanupManager.start();
      console.log("▶️ Cleanup manager started");
    },

    getCleanupStats() {
      return cleanupManager.getCleanupStats();
    },

    // Help function
    help() {
      console.group("🛠️ Artemis Cleanup Utilities");
      console.log("Available commands:");
      console.log(
        "• artemisCleanup.getStorageStatus() - Get current storage status"
      );
      console.log("• artemisCleanup.cleanupAll() - Perform full cleanup");
      console.log(
        "• artemisCleanup.cleanupOldFiles(days) - Clean files older than X days"
      );
      console.log(
        "• artemisCleanup.cleanupOrphanedFiles() - Clean orphaned files"
      );
      console.log(
        "• artemisCleanup.cleanupRoute(routeId) - Clean specific route"
      );
      console.log(
        "• artemisCleanup.cleanupByRoute(routeId) - Clean files by route ID"
      );
      console.log("• artemisCleanup.monitorStorage() - Get storage report");
      console.log(
        "• artemisCleanup.stopCleanupManager() - Stop automatic cleanup"
      );
      console.log(
        "• artemisCleanup.startCleanupManager() - Start automatic cleanup"
      );
      console.log(
        "• artemisCleanup.getCleanupStats() - Get cleanup statistics"
      );
      console.groupEnd();
    },
  };

  // Show help on first load
  console.log(
    "🛠️ Artemis Cleanup Utilities loaded. Type 'artemisCleanup.help()' for available commands."
  );
}

// Export for use in other modules
export {
  cleanupManager,
  storageManager,
  cleanupUploadedFiles,
  cleanupFailedFiles,
  cleanupOrphanedFiles,
};
